import { z } from "zod"

import { branchIdSchema, entityIdSchema } from "./identifiers.js"

const text = (maximum: number) => z.string().trim().min(1).max(maximum)
const instant = z.string().datetime({ offset: false })

export const supportCaseStateV1Schema = z.enum([
  "open",
  "in_progress",
  "waiting_on_customer",
  "resolved",
  "closed",
])

export const supportCasePriorityV1Schema = z.enum([
  "low",
  "normal",
  "high",
  "urgent",
])

export const supportCaseCreateV1Schema = z.object({
  supportCaseId: entityIdSchema,
  customerId: entityIdSchema,
  subject: text(240),
  description: text(10_000),
  priority: supportCasePriorityV1Schema,
  responseDueAt: instant,
  resolutionDueAt: instant,
}).strict()

export const supportCaseShareV1Schema = z.object({
  sharedBranchIds: z.array(branchIdSchema).max(50).superRefine((value, context) => {
    if (new Set(value).size !== value.length) {
      context.addIssue({ code: "custom", message: "Shared Branches must be unique." })
    }
  }),
  reason: text(1_000),
}).strict()

export const supportCaseAssignV1Schema = z.object({
  assigneeMembershipId: entityIdSchema.nullable(),
  reason: text(1_000),
}).strict()

export const supportCaseCustomerMessageCreateV1Schema = z.object({
  messageId: entityIdSchema,
  body: text(10_000),
  occurredAt: instant,
}).strict()

export const supportCaseInternalNoteCreateV1Schema = z.object({
  noteId: entityIdSchema,
  body: text(10_000),
  occurredAt: instant,
}).strict()

export const supportCaseCustomerWaitEnterV1Schema = z.object({
  waitId: entityIdSchema,
  reason: text(2_000),
  startedAt: instant,
}).strict()

export const supportCaseCustomerWaitExitV1Schema = z.object({
  waitId: entityIdSchema,
  resumedAt: instant,
}).strict()

export const supportCaseResolveV1Schema = z.object({
  resolution: text(10_000),
  resolvedAt: instant,
}).strict()

export const supportCaseCustomerProjectionV1Schema = z.object({
  supportCaseId: entityIdSchema,
  state: supportCaseStateV1Schema,
  subject: text(240),
  messages: z.array(z.object({
    messageId: entityIdSchema,
    body: text(10_000),
    occurredAt: instant,
  }).strict()).max(500),
  resolution: z.string().trim().max(10_000).nullable(),
}).strict()

export const supportCaseStaffProjectionV1Schema = z.object({
  supportCaseId: entityIdSchema,
  recordVersion: z.number().int().positive(),
  customerId: entityIdSchema,
  owningBranchId: branchIdSchema,
  sharedBranchIds: z.array(branchIdSchema).max(50),
  assigneeMembershipId: entityIdSchema.nullable(),
  state: supportCaseStateV1Schema,
  priority: supportCasePriorityV1Schema,
  subject: text(240),
  description: text(10_000),
  responseDueAt: instant,
  resolutionDueAt: instant,
  slaPaused: z.boolean(),
  slaPausedTotalSeconds: z.number().int().nonnegative(),
  customerMessages: z.array(z.object({
    messageId: entityIdSchema,
    body: text(10_000),
    occurredAt: instant,
  }).strict()).max(500),
  internalNotes: z.array(z.object({
    noteId: entityIdSchema,
    body: text(10_000),
    occurredAt: instant,
  }).strict()).max(500),
  resolution: z.string().trim().max(10_000).nullable(),
}).strict()

export const supportCaseBoardV1Schema = z.object({
  projectionVersion: z.literal(1),
  generatedAt: instant,
  items: z.array(supportCaseStaffProjectionV1Schema).max(200),
  complete: z.boolean(),
}).strict()

export type SupportCaseCreateV1 = z.infer<typeof supportCaseCreateV1Schema>
export type SupportCaseStaffProjectionV1 = z.infer<
  typeof supportCaseStaffProjectionV1Schema
>
export type SupportCaseCustomerProjectionV1 = z.infer<
  typeof supportCaseCustomerProjectionV1Schema
>
export type SupportCaseBoardV1 = z.infer<typeof supportCaseBoardV1Schema>
